import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createBusinessProfile, getBusinessProfile } from '../services/api';
import BusinessNameSearch from '../components/BusinessProfile/BusinessNameSearch';
import { useResponsive } from '../hooks/useResponsive';

function BusinessProfile() {
  const [profile, setProfile] = useState({
    businessName: '',
    businessType: '',
    description: '',
    phone: '',
    website: '',
    address: '',
    googleReviewLink: '',
    googlePlaceId: ''
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  // Load existing profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data } = await getBusinessProfile();
      setProfile({
        businessName: data.business_name || '',
        businessType: data.business_type || '',
        description: data.description || '',
        phone: data.phone || '',
        website: data.website || '',
        address: data.address || '',
        googleReviewLink: data.google_review_link || '',
        googlePlaceId: data.google_place_id || ''
      });
    } catch (error) {
      // Profile doesn't exist yet, that's okay
      console.log('No existing profile found');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleBusinessSelect = (businessData) => {
    setProfile({
      ...profile,
      businessName: businessData.name,
      address: businessData.address,
      phone: businessData.phone || profile.phone,
      website: businessData.website || profile.website,
      googleReviewLink: businessData.googleUrl || profile.googleReviewLink,
      googlePlaceId: businessData.placeId || profile.googlePlaceId
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createBusinessProfile(profile);
      alert('Profile saved successfully!');
      navigate('/managers');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save profile');
    }
  };

  return (
    <div className="container" style={{ padding: isMobile ? '20px 16px' : '32px 20px' }}>
      <h1 style={{ 
        marginBottom: isMobile ? '24px' : '32px',
        fontSize: isMobile ? '24px' : '32px'
      }}>Business Profile Setup</h1>
      
      {loading ? (
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: isMobile ? '40px 20px' : '60px' 
        }}>
          <p>Loading profile...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: isMobile ? '20px' : '28px' }}>
          <h3 style={{ 
            marginBottom: '20px',
            fontSize: isMobile ? '18px' : '20px'
          }}>Business Profile</h3>
          
          <label>Business Name</label>
          <BusinessNameSearch 
            onSelect={handleBusinessSelect}
            initialValue={profile.businessName}
          />

          <label>Business Type</label>
          <select name="businessType" value={profile.businessType} onChange={handleChange} required>
            <option value="">Select Business Type</option>
            <option value="restaurant">🍽️ Restaurant</option>
            <option value="cafe">☕ Café</option>
            <option value="salon">💇 Salon</option>
            <option value="spa">🧖 Spa</option>
            <option value="hotel">🏨 Hotel</option>
            <option value="retail">🛍️ Retail Store</option>
            <option value="service">🔧 Service Business</option>
            <option value="clinic">🏥 Clinic</option>
            <option value="hospital">🏥 Hospital</option>
            <option value="gym">💪 Gym & Fitness</option>
            <option value="bar">🍺 Bar & Pub</option>
            <option value="automotive">🚗 Automotive</option>
            <option value="healthcare">⚕️ Healthcare</option>
            <option value="education">📚 Education</option>
            <option value="professional_services">💼 Professional Services</option>
            <option value="beauty">💄 Beauty & Cosmetics</option>
            <option value="dental">🦷 Dental</option>
            <option value="real_estate">🏠 Real Estate</option>
            <option value="travel">✈️ Travel & Tourism</option>
            <option value="photography">📷 Photography</option>
            <option value="event_planning">🎉 Event Planning</option>
            <option value="pet_services">🐾 Pet Services</option>
            <option value="supermarket">🛒 Supermarket</option>
            <option value="pharmacy">💊 Pharmacy</option>
            <option value="construction">🏗️ Construction</option>
            <option value="manufacturing">🏭 Manufacturing</option>
            <option value="transportation">🚚 Transportation & Logistics</option>
            <option value="entertainment">🎭 Entertainment</option>
            <option value="tech">💻 Tech & Software</option>
            <option value="finance">💰 Finance & Banking</option>
            <option value="law_firm">⚖️ Law Firm</option>
            <option value="cleaning">🧹 Cleaning Services</option>
            <option value="nonprofit">❤️ Nonprofit Organization</option>
            <option value="government">🏛️ Government Office</option>
            <option value="warehouse">📦 Warehouse</option>
            <option value="agriculture">🌾 Agriculture</option>
            <option value="bakery">🥐 Bakery</option>
            <option value="nightclub">🎶 Nightclub</option>
            <option value="bookstore">📖 Bookstore</option>
            <option value="florist">🌸 Florist</option>
            <option value="veterinary">🐶 Veterinary Clinic</option>
            <option value="other">⭐ Other</option>
          </select>

          <label>Business Description</label>
          <textarea
            name="description"
            value={profile.description}
            onChange={handleChange}
            rows="4"
            required
          />
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px' }}>Contact Information</h3>
          
          <label>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={profile.phone}
            onChange={handleChange}
            required
          />

          <label>Website URL</label>
          <input
            type="url"
            name="website"
            value={profile.website}
            onChange={handleChange}
          />

          <label>Address</label>
          <textarea
            name="address"
            value={profile.address}
            onChange={handleChange}
            rows="3"
            required
          />
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px' }}>Review Settings</h3>
          
          <label>Google Review Link</label>
          <input
            type="url"
            name="googleReviewLink"
            value={profile.googleReviewLink}
            onChange={handleChange}
            placeholder="https://g.page/r/..."
            required
          />
        </div>

        <button type="submit" className="btn btn-primary">
          {profile.businessName ? 'Update Profile' : 'Create Profile'}
        </button>
      </form>
      )}
    </div>
  );
}

export default BusinessProfile;
